import { useQuery } from '@tanstack/react-query';
import type {
  AnalyticsBreakdowns,
  AnalyticsLeads,
  AnalyticsSummary,
  AnalyticsUpcoming,
} from '@mys/contracts';
import { api } from './api.js';

/**
 * The summary as THIS BUILD may actually receive it.
 *
 * The API requires `leads` — slice 1.7a's contract says so and the server
 * always sends it. This dashboard cannot assume it, because the two do not
 * deploy together: Cloudflare Pages took about 25 minutes to publish the admin
 * build on 2026-10-13, and for those minutes a live dashboard is talking to
 * the previous API. Typed as required, `data.leads.today` would throw and take
 * the whole analytics screen down with it — which is exactly what `earlier` on
 * the lead page did in slice 1.6a, found the same way: by a test fixture that
 * predated the field.
 *
 * So the field is optional HERE and required in the contract. The screen
 * renders the section only when it arrives.
 */
export type AnalyticsResponse = Omit<AnalyticsSummary, 'leads' | 'breakdowns' | 'upcoming'> & {
  leads?: AnalyticsLeads | undefined;
  breakdowns?: AnalyticsBreakdowns | undefined;
  upcoming?: AnalyticsUpcoming | undefined;
};

/* ============================================================================
   The overview's and /analytics' numbers — one query, two screens.
   ========================================================================== */

/**
 * Both screens read this, under the same key, so TanStack Query serves the
 * second one from cache and the two cannot show different figures for the same
 * morning. `staleTime` is deliberate: these are day-scale numbers and
 * refetching them on every window focus would spend a shop owner's data to
 * redraw the same tiles.
 */
/** @param enabled false without `analytics:view` — see useLeads. */
export function useAnalytics(enabled = true) {
  return useQuery({
    queryKey: ['analytics'],
    queryFn: ({ signal }) => api.get<AnalyticsResponse>('/api/analytics/summary', signal),
    staleTime: 60_000,
    enabled,
  });
}

/** A month label as `MMM` in the owner's locale, for a chart axis. */
export function monthLabel(month: string): string {
  const [year, index] = month.split('-');
  if (!year || !index) return month;
  const date = new Date(Date.UTC(Number(year), Number(index) - 1, 1));
  return new Intl.DateTimeFormat('en-IN', { month: 'short', timeZone: 'UTC' }).format(date);
}

/**
 * Each value's share of the largest, 0–1, for a bar's width.
 *
 * Scaled to the biggest row rather than to a fixed ceiling, because the useful
 * comparison is between the rows on screen and nothing else. All-zero returns
 * zeroes rather than dividing by zero — a new business has no bars, which is
 * honest.
 *
 * Plain numbers since slice 1.7b, where the same drawing took on leads by
 * source and sales by plan. It briefly had a second copy of this rule inside
 * the shared component, which is how a chart ends up scaling two ways on one
 * screen; ESLint caught it as an unused import, which is luckier than it
 * sounds.
 */
export function barScale(values: readonly number[]): number[] {
  const peak = Math.max(0, ...values);
  return values.map((value) => (peak === 0 ? 0 : value / peak));
}

/**
 * Month-on-month change as a percentage, or null when there is nothing to
 * compare against.
 *
 * Null rather than 0 or 100 when last month was empty: "up 100%" from zero is
 * a number that means nothing, and this screen's whole premise is that every
 * figure on it can be acted on.
 */
export function changePercent(thisMonth: number, lastMonth: number): number | null {
  if (lastMonth === 0) return null;
  return Math.round(((thisMonth - lastMonth) / lastMonth) * 100);
}
